import React from 'react';
import { Badge, Button } from 'react-bootstrap';
import { AlertTriangle, ChevronDown, KeyRound, Nfc, Paperclip } from 'lucide-react';
import { KeyHolder, KeyType, KEY_KIND_LABELS } from '../../types/keyRegister';
import KeyStatusBar from './KeyStatusBar';
import KeyTable from './KeyTable';
import KeyTypeActionsMenu from './KeyTypeActionsMenu';
import KeyTypeFiles from './KeyTypeFiles';
import { KeyTypeSection as Section } from './keyRegisterView';
import { keyCountLabel, lostCountLabel, overdueCountLabel } from './keyRegisterLabels';

interface KeyTypeSectionProps {
    section: Section;
    /** Whether the type has keys at all (scrapped included); a type is deletable only without keys. */
    hasAnyKeys: boolean;
    buildingNames: string[];
    /** The lägenheter, lokaler and förråd it opens ("Hus B: Lägenhet 1001, Förråd 12"). */
    objectsLabel?: string;
    expanded: boolean;
    today: string;
    disabled: boolean;
    holderName: (holder: KeyHolder | undefined) => string;
    getFileUrl: (storagePath: string) => Promise<string>;
    onToggle: () => void;
    onSelectKey: (keyKey: string) => void;
    onEdit: () => void;
    onAddKeys: () => void;
    onToggleArchive: () => void;
    onDelete: () => void;
}

/** Key or fob (tag) icon; the kind is its title and accessible name. */
const KeyKindIcon: React.FC<{ kind: KeyType['kind'] }> = ({ kind }) => {
    const Icon = kind === 'tag' ? Nfc : KeyRound;
    return (
        <span className="key-section-kind" title={KEY_KIND_LABELS[kind]} role="img" aria-label={KEY_KIND_LABELS[kind]}>
            <Icon size={18} aria-hidden="true" />
        </span>
    );
};

/**
 * One key type: a summary row that toggles the type's key table. The actions menu sits next to
 * the toggle, never inside it. The summary always describes the whole type, even while filtering.
 */
const KeyTypeSection: React.FC<KeyTypeSectionProps> = ({
    section, hasAnyKeys, buildingNames, objectsLabel, expanded, today, disabled, holderName, getFileUrl,
    onToggle, onSelectKey, onEdit, onAddKeys, onToggleArchive, onDelete,
}) => {
    const { type, scopeKeys, rows, summary } = section;
    const bodyId = `key-section-body-${type.key}`;
    const opens = [type.opens, buildingNames.length > 0 ? buildingNames.join(', ') : undefined, objectsLabel].filter(Boolean).join(' · ');
    const lost = summary.counts.lost;
    const fileCount = Object.keys(type.attachments ?? {}).length;

    return (
        <section className={`key-section${type.archived ? ' key-section--archived' : ''}${expanded ? ' key-section--open' : ''}`} data-testid={`key-type-${type.key}`}>
            <div className="key-section-head">
                <button
                    type="button"
                    className="key-section-toggle"
                    aria-expanded={expanded}
                    aria-controls={bodyId}
                    onClick={onToggle}
                    data-testid={`key-section-toggle-${type.key}`}
                >
                    <ChevronDown size={20} className="key-section-chevron" aria-hidden="true" />
                    <KeyKindIcon kind={type.kind} />
                    <span className="key-section-main">
                        <span className="key-section-name">
                            {type.name}
                            {type.archived && <Badge bg="secondary" className="ms-2">Arkiverad</Badge>}
                        </span>
                        {opens && <span className="key-section-opens">Öppnar: {opens}</span>}
                        {fileCount > 0 && (
                            <span className="key-section-file-count">
                                <Paperclip size={13} aria-hidden="true" />
                                {fileCount === 1 ? '1 fil' : `${fileCount} filer`}
                            </span>
                        )}
                        {lost > 0 && (
                            <span className="key-section-loss">
                                <AlertTriangle size={14} aria-hidden="true" />
                                {lostCountLabel(lost)}
                                {type.kind === 'mechanical' && ' · Mekaniska nycklar kan inte spärras'}
                            </span>
                        )}
                    </span>
                    <span className="key-section-status">
                        {summary.total === 0 ? (
                            <span className="key-section-count">{keyCountLabel(0)}</span>
                        ) : (
                            <>
                                <KeyStatusBar counts={summary.counts} className="key-section-bar" />
                                <span className="key-section-count">{summary.counts.handedOut} av {summary.total} ute</span>
                            </>
                        )}
                        {summary.overdue > 0 && <span className="key-overdue-badge">{overdueCountLabel(summary.overdue)}</span>}
                    </span>
                </button>
                {/* A sibling of the toggle; clicks here never reach the section. */}
                <div className="key-section-actions" onClick={e => e.stopPropagation()}>
                    <KeyTypeActionsMenu
                        archived={!!type.archived}
                        canDelete={!hasAnyKeys}
                        disabled={disabled}
                        onEdit={onEdit}
                        onAddKeys={onAddKeys}
                        onToggleArchive={onToggleArchive}
                        onDelete={onDelete}
                    />
                </div>
            </div>
            {expanded && (
                <div className="key-section-body" id={bodyId}>
                    <KeyTypeFiles type={type} getUrl={getFileUrl} />
                    {rows.length > 0 ? (
                        <KeyTable keys={rows} today={today} holderName={holderName} onSelectKey={onSelectKey} />
                    ) : (
                        <p className="key-section-empty">
                            {scopeKeys.length === 0 && hasAnyKeys ? 'Alla exemplar är kasserade.' : 'Inga exemplar ännu.'}
                            {!type.archived && (
                                <>
                                    {' '}
                                    <Button variant="link" className="key-section-add" onClick={onAddKeys} disabled={disabled}>
                                        Lägg till exemplar
                                    </Button>
                                </>
                            )}
                        </p>
                    )}
                </div>
            )}
        </section>
    );
};

export default KeyTypeSection;
